import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { emailApi, authApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, 
  Loader2, 
  Mail, 
  AlertCircle,
  ArrowRight,
  Search
} from 'lucide-react';
import { toast } from 'sonner';

const OutlookConnected = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser } = useAuth();
  const hasStartedScan = useRef(false);
  
  const [status, setStatus] = useState('connecting'); // connecting, scanning, success, error
  const [connectedEmail, setConnectedEmail] = useState('');
  const [subscriptionsFound, setSubscriptionsFound] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const connectionId = searchParams.get('connection_id');
    const email = searchParams.get('email');
    
    if (email) {
      setConnectedEmail(email);
    }

    if (connectionId && !hasStartedScan.current) {
      hasStartedScan.current = true;
      startAutoScan(connectionId);
    } else if (!connectionId) {
      setStatus('error');
      setErrorMessage('No connection ID found');
    }
  }, [searchParams]);

  const startAutoScan = async (connectionId) => {
    setStatus('scanning');
    
    try {
      const response = await emailApi.scanOutlook(connectionId);
      const found = response.data.subscriptions_created || response.data.subscriptions_detected || 0;
      setSubscriptionsFound(found);
      setStatus('success');
      
      if (found > 0) {
        toast.success(`Found and saved ${found} subscription${found > 1 ? 's' : ''}!`);
      } else {
        toast.info('Scan complete - no new subscriptions found');
      }
    } catch (error) {
      console.error('Outlook scan error:', error);
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || 'Failed to scan emails');
      toast.error('Failed to scan emails');
    }
  };

  const handleContinue = () => {
    // Check if user was in onboarding
    const wasOnboarding = localStorage.getItem('subguard_onboarding_step');
    localStorage.removeItem('subguard_onboarding_step');
    localStorage.removeItem('subguard_onboarding_country');
    
    if (wasOnboarding && !user?.onboarding_completed) {
      navigate('/onboarding');
    } else {
      navigate('/dashboard');
    }
  };

  const handleGoToDashboard = async () => {
    localStorage.removeItem('subguard_onboarding_step');
    localStorage.removeItem('subguard_onboarding_country');
    
    // Mark onboarding as complete and refresh user data
    try {
      await authApi.updateProfile({ onboarding_completed: true });
      const userResponse = await authApi.getMe();
      updateUser(userResponse.data);
      // Small delay to ensure state is updated
      setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 100);
    } catch (error) {
      console.error('Failed to update onboarding status:', error);
      navigate('/dashboard', { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-6" data-testid="outlook-connected-page">
      <div className="w-full max-w-lg">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
            alt="SubGuard" 
            className="w-12 h-12 rounded-xl"
          />
          <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
            SubGuard
          </span>
        </div>

        <Card className="border-0 shadow-xl">
          <CardContent className="p-8">
            {/* Connecting State */}
            {status === 'connecting' && (
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                  Connecting Outlook...
                </h1>
                <p className="text-gray-500">Please wait while we set up your connection</p>
              </div>
            )}

            {/* Scanning State */}
            {status === 'scanning' && (
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-blue-600 animate-pulse" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                  Scanning Your Inbox
                </h1>
                <p className="text-gray-500 mb-4">Looking for subscription emails from the last 60 days...</p>
                {connectedEmail && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span>{connectedEmail}</span>
                  </div>
                )}
                <div className="mt-6">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{ width: '60%' }}></div>
                  </div>
                </div>
              </div>
            )}

            {/* Success State */}
            {status === 'success' && (
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-blue-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                  Scan Complete!
                </h1>
                {subscriptionsFound > 0 ? (
                  <p className="text-gray-500 mb-6">
                    We found <span className="font-bold text-blue-600">{subscriptionsFound}</span> subscription{subscriptionsFound > 1 ? 's' : ''} in your inbox
                  </p>
                ) : (
                  <p className="text-gray-500 mb-6">
                    No new subscriptions found. You can add them manually from your dashboard.
                  </p>
                )}
                
                {connectedEmail && (
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-600 mb-6">
                    <Mail className="w-4 h-4" />
                    <span>{connectedEmail} connected</span>
                  </div>
                )}

                <Button
                  onClick={handleGoToDashboard}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Error State */}
            {status === 'error' && (
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                  Something Went Wrong
                </h1>
                <p className="text-gray-500 mb-6">
                  {errorMessage || 'Failed to scan your inbox. Please try again.'}
                </p>
                
                <div className="space-y-3">
                  <Button
                    onClick={handleContinue}
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  >
                    Continue Anyway
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <button
                    onClick={() => navigate('/settings')}
                    className="w-full text-sm text-gray-500 hover:text-gray-700 py-2"
                  >
                    Go to Settings
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OutlookConnected;
